import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import useFlash from "go-alert/AlertMessage";
import Header from "go-app/components/Header";
import handleError from "go-app/services/errors";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { LoadingContainer } from "go-core/components/Loading";
import RouteHistory from "go-core/utils/routeHistory";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { CustomFieldTemplateApi } from "go-segment/services/types";
import { apiOrganization } from "../../../../../../../../../../services/Api/Organization/apiOrganization";
import {
	CompanyApi,
	InvoiceApi,
	InvoiceDetailsApi,
} from "../../../../../../../../../../services/Api/Organization/types";
import CompanyInfoNotFullAlert from "../../Alerts/CompanyInfoNotFullAlert";
import NoInvoiceDetailsAlert from "../../Alerts/NoInvoiceDetailsAlert";
import InvoiceForm from "../../components/InvoiceForm/InvoiceForm";
import {
	checkIfCanRenderInvoiceForm,
	checkIfRenderNoInvoiceDetailsAlert,
	checkIfRenderNotFullCompanyInfo,
} from "../../services";

interface MatchParams {
	invoiceId: string;
}

const OrganizationDocumentInvoiceEditPage = () => {
	const [customFieldsConfig, setCustomFieldsConfig] = useState<CustomFieldTemplateApi[] | undefined>();
	const [invoice, setInvoice] = useState<InvoiceApi | undefined>();
	const [invoiceDetails, setInvoiceDetails] = useState<InvoiceDetailsApi | undefined>();
	const [companyInfo, setCompanyInfo] = useState<CompanyApi | undefined>();
	const { addFlash } = useFlash();
	const { t } = useTranslation();
	const { handleChangeTabTitle } = useBrowserTabTitle();
	const { invoiceId } = useParams<MatchParams>();
	const history = useHistory();
	const organization = useSelector(selectOrganization);

	useEffect(() => {
		init();
	}, []);

	const init = async () => {
		await fetchResources();
		await fetchInvoice();
		await fetchInvoiceDetails();
		await fetchCompanyInfo();
	};

	const fetchResources = async () => {
		try {
			const params = {
				resource_name: "INVOICE",
				custom_field_resource_type: "INVOICE",
				custom_field_permission_scope: "WRITE",
			};
			const res = await apiOrganization.getResourceTypes(params);
			setCustomFieldsConfig(res.custom_fields);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const fetchInvoice = async () => {
		try {
			const res = await apiOrganization.getInvoice(Number(invoiceId), { include: "all" });
			setInvoice(res);
			const invoiceNumber =
				res.status === "DRAFT"
					? `${t("enums.invoices.document_type.PROFORMA")} #${res.id}`
					: `${t("enums.invoices.document_type.INVOICE")} #${res.document_number.full_number}`;
			handleChangeTabTitle(`${invoiceNumber} | ${t("modules.invoice.header.title")}`);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const fetchInvoiceDetails = async () => {
		try {
			const res = await apiOrganization.getInvoiceDetails();
			setInvoiceDetails(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const fetchCompanyInfo = async () => {
		try {
			const res = await apiOrganization.getCompany({ include: "all" });
			setCompanyInfo(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	if (!invoice || !customFieldsConfig || !invoiceDetails || !companyInfo) return <LoadingContainer />;

	const goBack = () => RouteHistory.goBack(history, `/${organization.id}/documents/invoices/${invoice.id}`);
	const title = `${t("modules.invoice.header.title")} ${
		invoice.status === "DRAFT"
			? `${t("enums.invoices.document_type.PROFORMA")} #${invoice.id}`
			: `${t("enums.invoices.document_type.INVOICE")} #${invoice.document_number.full_number}`
	}`;

	return (
		<>
			<Header title={title} back={goBack} />
			{checkIfRenderNoInvoiceDetailsAlert(invoiceDetails) && <NoInvoiceDetailsAlert />}
			{checkIfRenderNotFullCompanyInfo(companyInfo) && <CompanyInfoNotFullAlert />}
			{checkIfCanRenderInvoiceForm(invoiceDetails, companyInfo)}
			<InvoiceForm invoice={invoice} customFieldsConfig={customFieldsConfig} />
		</>
	);
};

export default OrganizationDocumentInvoiceEditPage;
